import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdministrationRequestApproveAPI } from '@innowise-group/core';
import { Modal, modalHoc, ModalProps, notificationsManager } from '@innowise-group/mui-kit';

import * as Styled from './request-approve-modal.styles';

interface RequestApproveModalProps {
  id: number;
  vacancyName: string;
  departmentName: string;
  isReApprove?: boolean;
  onOperationCompleted?: (id?: number) => void;
}

const RequestApproveModal: React.FC<ModalProps & RequestApproveModalProps> = ({
  close,
  id,
  isReApprove,
  vacancyName,
  departmentName,
  onOperationCompleted,
}) => {
  const { t } = useTranslation();
  const [isBusy, setBusy] = useState(false);

  const { setAdministrativeRequestClarify, setAdministrativeRequestReClarify } = useAdministrationRequestApproveAPI();

  const handleProcess = useCallback(() => {
    setBusy(true);

    if (isReApprove) {
      setAdministrativeRequestReClarify(id)
        .then(() => {
          onOperationCompleted(id);
          close();
        })
        .catch(() => {
          notificationsManager.error({
            title: t('pages.requests.approvalModal.notReapproved'),
            subtitle: t('notifications.text.networkError'),
          });
        })
        .finally(() => setBusy(false));
    } else {
      setAdministrativeRequestClarify(id, true)
        .then(() => {
          onOperationCompleted(id);
          close();
        })
        .catch(() => {
          notificationsManager.error({
            title: t('pages.requests.approvalModal.notApproved'),
            subtitle: t('notifications.text.networkError'),
          });
        })
        .finally(() => setBusy(false));
    }
  }, [id, isReApprove, onOperationCompleted, setAdministrativeRequestClarify]);

  return (
    <Modal.Container size="small">
      <Modal.Header onClose={close}>
        <Styled.ModalTitle>{t('pages.requests.approvalModal.confirmApproval')}</Styled.ModalTitle>
      </Modal.Header>
      <Modal.Body>
        <Styled.Description>
          <div>{t('pages.requests.approvalModal.requestWillOpenedAfterApproval', { request: id })}</div>
          <div>{t('pages.requests.approvalModal.vacancy', { vacancy: vacancyName })}</div>
          <div>{t('pages.requests.approvalModal.department', { department: departmentName })}</div>
        </Styled.Description>
      </Modal.Body>
      <Styled.Footer>
        <Styled.CancelButton onClick={close} variant="outlined" disabled={isBusy}>
          {t('buttons.cancel')}
        </Styled.CancelButton>
        <Styled.SubmitButton onClick={handleProcess} disabled={isBusy}>
          {t('buttons.approve')}
        </Styled.SubmitButton>
      </Styled.Footer>
    </Modal.Container>
  );
};

export default modalHoc(RequestApproveModal, 'request-approve');
